import { useEffect } from 'react';
import { useEditor } from 'tldraw';
import { useBoardStore } from '../store/useBoardStore';
import {
  digestDeleteListenersSuppressed,
  enqueueDigestAddFromStore,
  enqueueDigestEventsFromStore,
} from '../utils/boardConstraintsDigest';
import { isFrameNameOnlyUpdate } from '../utils/boardDocumentChanges';
import { membershipIdFromFrameShape } from '../utils/frameMembership';
import {
  buildFrameDeleteEvent,
  buildPinMembershipAddEvent,
  isMembershipPinType,
  membershipSnapshotKey,
  pinsInFrame,
  pinsWithStaleFrameId,
  readClusterMembership,
} from '../utils/frameClusterDigest';

const CLUSTER_DIGEST_DEBOUNCE_MS = 400;

/**
 * Re-digest pins when they move into / out of a frame, when a frame is renamed,
 * or when a frame is deleted (revoke cluster claims; re-assert pin rows).
 */
export default function FrameClusterDigest() {
  const editor = useEditor();
  const canEdit = useBoardStore(s => Boolean(s.canEdit || s.isOwner));

  useEffect(() => {
    if (!editor || !canEdit) return undefined;

    const timers = new Map();
    const committed = new Map();

    function snapshotOf(shape) {
      return readClusterMembership(editor, shape);
    }

    function enqueuePin(shape) {
      if (!shape) return;
      const activities = useBoardStore.getState().activities || [];
      const event = buildPinMembershipAddEvent(editor, shape, activities);
      if (!event) return;
      enqueueDigestAddFromStore([event]);
      const snap = snapshotOf(shape);
      if (snap) committed.set(shape.id, membershipSnapshotKey(snap));
    }

    function schedulePin(shapeId) {
      clearTimeout(timers.get(shapeId));
      timers.set(
        shapeId,
        setTimeout(() => {
          timers.delete(shapeId);
          const shape = editor.getShape(shapeId);
          if (!shape || !isMembershipPinType(shape.type)) return;
          const snap = snapshotOf(shape);
          const nextKey = membershipSnapshotKey(snap);
          if (committed.get(shapeId) === nextKey) return;
          enqueuePin(shape);
        }, CLUSTER_DIGEST_DEBOUNCE_MS)
      );
    }

    function scheduleFrameMembers(frameId, shapes) {
      const list = Array.isArray(shapes) ? shapes : pinsInFrame(editor, frameId);
      for (const shape of list) schedulePin(shape.id);
    }

    function seedExisting() {
      for (const shape of editor.getCurrentPageShapes() || []) {
        if (!isMembershipPinType(shape?.type)) continue;
        const snap = snapshotOf(shape);
        if (snap) committed.set(shape.id, membershipSnapshotKey(snap));
      }
    }

    const unlisten = editor.store.listen(
      (entry) => {
        const changes = entry?.changes || {};

        for (const record of Object.values(changes.removed || {})) {
          if (record?.typeName !== 'shape') continue;
          if (isMembershipPinType(record.type)) {
            clearTimeout(timers.get(record.id));
            timers.delete(record.id);
            committed.delete(record.id);
            continue;
          }
          if (record.type !== 'frame') continue;
          const frameId = membershipIdFromFrameShape(record) || String(record.id);
          const members = pinsWithStaleFrameId(editor, frameId);
          const events = [];
          if (!digestDeleteListenersSuppressed.active) {
            const del = buildFrameDeleteEvent(record);
            if (del) events.push(del);
          }
          const activities = useBoardStore.getState().activities || [];
          for (const shape of members) {
            committed.delete(shape.id);
            const add = buildPinMembershipAddEvent(editor, shape, activities);
            if (add) events.push(add);
            const snap = readClusterMembership(editor, shape);
            if (snap) committed.set(shape.id, membershipSnapshotKey(snap));
          }
          if (events.length) enqueueDigestEventsFromStore(events);
        }

        for (const record of Object.values(changes.added || {})) {
          if (record?.typeName !== 'shape') continue;
          if (!isMembershipPinType(record.type)) continue;
          const snap = snapshotOf(record);
          if (snap) committed.set(record.id, membershipSnapshotKey(snap));
        }

        for (const tuple of Object.values(changes.updated || {})) {
          const prev = Array.isArray(tuple) ? tuple[0] : null;
          const next = Array.isArray(tuple) ? tuple[1] : tuple;
          if (next?.typeName !== 'shape') continue;

          if (isMembershipPinType(next.type)) {
            const prevFrame = String(prev?.meta?.frameId || '');
            const nextFrame = String(next.meta?.frameId || '');
            if (prevFrame !== nextFrame) {
              schedulePin(next.id);
            }
            continue;
          }

          if (next.type === 'frame' && isFrameNameOnlyUpdate(prev, next)) {
            const frameId = membershipIdFromFrameShape(next) || String(next.id);
            scheduleFrameMembers(frameId);
          }
        }
      },
      { scope: 'document' }
    );

    const seedSoon = setTimeout(seedExisting, 400);

    return () => {
      unlisten();
      clearTimeout(seedSoon);
      for (const timer of timers.values()) clearTimeout(timer);
      timers.clear();
    };
  }, [editor, canEdit]);

  return null;
}
